import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
} from "@common/types/auditLog";
import { HistoryField } from "@common/constants/history-source-types";
import { CATALOG_ITEM_FIELDS } from "@common/constants/history/catalog-item-sources";
import { isString } from "@common/helpers/typeGuards";
import {
    getHistoryFieldLabel,
    getPreferredSnapshot,
} from "../../utils/historyDetailsHelpers";
import { HistoryCatalogItemTableKind } from "../views/historyCatalogItemDetailsView";
import { HistoryCatalogItemDetailsRow } from "../views/historyCatalogItemDetailsViewConfig";
import {
    ClassLabelResolver,
    formatCatalogItemValue,
} from "./historyCatalogItemValues";

const EMPTY_TEXT = "-";
const FALLBACK_ITEM_NAME = "Catalog item";

const CATALOG_ITEM_FIELD_NAMES: readonly string[] = CATALOG_ITEM_FIELDS;

export interface HistoryCatalogItemTable {
    kind: HistoryCatalogItemTableKind;
    rows: HistoryCatalogItemDetailsRow[];
}

interface BuildHistoryCatalogItemTableParams {
    logs: HistoryDetailsItem[];
    selectedFields: string[];
    resolveClassLabel: ClassLabelResolver;
}

interface BuildRowsParams {
    log: HistoryDetailsItem;
    selectedFields: string[];
    resolveClassLabel: ClassLabelResolver;
}

const isNonEmptyString = <T>(value: T): value is T & string =>
    isString(value) && value.length > 0;

const capitalizeFirst = (text: string): string =>
    text.charAt(0).toUpperCase() + text.slice(1);

function getNameFromDiff(log: HistoryDetailsItem): string | undefined {
    const nameDiff = log.diff.find((diff) => diff.field === HistoryField.NAME);
    if (!nameDiff) {
        return undefined;
    }
    const ref =
        log.changeType === AuditLogChangeType.DELETE
            ? nameDiff.before
            : nameDiff.after;
    return isNonEmptyString(ref.value) ? ref.value : undefined;
}

export function resolveCatalogItemName(log: HistoryDetailsItem): string {
    const snapshotName = getPreferredSnapshot(log)?.name;
    if (isNonEmptyString(snapshotName)) {
        return snapshotName;
    }
    const resourceName = log.resource?.name;
    if (isNonEmptyString(resourceName)) {
        return resourceName;
    }
    return getNameFromDiff(log) ?? FALLBACK_ITEM_NAME;
}

function buildBaseRow(
    log: HistoryDetailsItem,
    rowId: string,
): Omit<HistoryCatalogItemDetailsRow, "fieldLabel" | "previous" | "modified"> {
    return {
        id: rowId,
        resourceType: log.resourceType,
        resourceId: log.resourceId,
        itemName: resolveCatalogItemName(log),
        logDate: log.createdAt,
        date: log.createdAt,
        entityId: log.entityId ?? null,
    };
}

function buildIdentityRow(
    log: HistoryDetailsItem,
): HistoryCatalogItemDetailsRow {
    const base = buildBaseRow(log, log.id);
    const isDelete = log.changeType === AuditLogChangeType.DELETE;
    return {
        ...base,
        fieldLabel: EMPTY_TEXT,
        previous: isDelete ? base.itemName : EMPTY_TEXT,
        modified: isDelete ? EMPTY_TEXT : base.itemName,
    };
}

function getDisplayableDiffs({
    log,
    selectedFields,
}: Pick<BuildRowsParams, "log" | "selectedFields">): AuditLogDiff[] {
    return log.diff.filter(
        (diff) =>
            CATALOG_ITEM_FIELD_NAMES.includes(diff.field) &&
            (selectedFields.length === 0 ||
                selectedFields.includes(diff.field)),
    );
}

function buildChangeRows({
    log,
    selectedFields,
    resolveClassLabel,
}: BuildRowsParams): HistoryCatalogItemDetailsRow[] {
    if (log.changeType !== AuditLogChangeType.UPDATE) {
        return [buildIdentityRow(log)];
    }
    return getDisplayableDiffs({ log, selectedFields }).map((diff) => ({
        ...buildBaseRow(log, `${log.id}-${diff.field}`),
        fieldLabel: capitalizeFirst(getHistoryFieldLabel(diff.field)),
        previous: formatCatalogItemValue(
            diff.field,
            diff.before,
            resolveClassLabel,
        ),
        modified: formatCatalogItemValue(
            diff.field,
            diff.after,
            resolveClassLabel,
        ),
    }));
}

export function buildHistoryCatalogItemTable({
    logs,
    selectedFields,
    resolveClassLabel,
}: BuildHistoryCatalogItemTableParams): HistoryCatalogItemTable {
    const hasUpdates = logs.some(
        (log) => log.changeType === AuditLogChangeType.UPDATE,
    );
    if (!hasUpdates) {
        return {
            kind: HistoryCatalogItemTableKind.IDENTITY,
            rows: logs.map(buildIdentityRow),
        };
    }
    return {
        kind: HistoryCatalogItemTableKind.CHANGE,
        rows: logs.flatMap((log) =>
            buildChangeRows({ log, selectedFields, resolveClassLabel }),
        ),
    };
}
